import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { FileText, HelpCircle, BarChart3, Calendar, Loader2, Lock, UserPlus, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../components/community/shared';
import CommunityHeader from '../components/community/CommunityHeader';
import CommunitySidebar from '../components/community/CommunitySidebar';
import WallTab from '../components/community/WallTab';
import QuestionsTab from '../components/community/QuestionsTab';
import PollsTab from '../components/community/PollsTab';
import CalendarTab from '../components/community/CalendarTab';
import EditCommunityModal from '../components/community/EditCommunityModal';
import ShareModal from '../components/ShareModal';

const VALID_TABS = ['wall', 'questions', 'polls', 'calendar'];

const CommunityPage = () => {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joinLoading, setJoinLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeTab, setActiveTab] = useState(() => (VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'wall'));

  // Wall state
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);

  // Members state
  const [allMembers, setAllMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);

  // Q&A state
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionFilter, setQuestionFilter] = useState('all');

  // Events state
  const [events, setEvents] = useState([]);

  // Polls state
  const [polls, setPolls] = useState([]);
  const [pollsLoading, setPollsLoading] = useState(false);

  useEffect(() => {
    loadCommunity();
  }, [communityId]);

  // Brief 27 oct LOT C.1 — le contenu n'est chargé que si la communauté est accessible.
  useEffect(() => {
    if (!community || community.content_locked) return;
    loadPosts();
    loadMembers();
  }, [community?.community_id, community?.content_locked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!community || community.content_locked) return;
    if (activeTab === 'questions') loadQuestions();
    if (activeTab === 'calendar') loadEvents();
    if (activeTab === 'polls') loadPolls();
  }, [activeTab, communityId, community?.content_locked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'questions' && community && !community.content_locked) loadQuestions();
  }, [questionFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCommunity = async () => {
    try {
      const response = await axios.get(`${API}/communities/${communityId}`, { withCredentials: true });
      setCommunity(response.data);
      setIsPending(!!response.data.has_pending_request);
    } catch (error) { console.error('Error loading community:', error); }
    finally { setLoading(false); }
  };

  const loadPosts = async () => {
    setPostsLoading(true);
    try {
      const response = await axios.get(`${API}/communities/${communityId}/posts`, { withCredentials: true });
      setPosts(response.data.posts || []);
    } catch (error) { console.error(error); }
    finally { setPostsLoading(false); }
  };

  const loadMembers = async () => {
    try {
      const response = await axios.get(`${API}/communities/${communityId}/members`, { withCredentials: true });
      setAllMembers(response.data.members || []);
      setMembersTotal(response.data.total || 0);
      setOnlineCount(response.data.online_count || 0);
    } catch (error) { console.error(error); }
  };

  const loadQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const response = await axios.get(`${API}/communities/${communityId}/questions`, {
        params: { status: questionFilter },
        withCredentials: true
      });
      setQuestions(response.data.questions || []);
    } catch (error) { console.error(error); }
    finally { setQuestionsLoading(false); }
  };

  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API}/communities/${communityId}/events`, { withCredentials: true });
      setEvents(response.data.events || []);
    } catch (error) { console.error(error); }
  };

  const loadPolls = async () => {
    setPollsLoading(true);
    try {
      const response = await axios.get(`${API}/communities/${communityId}/polls`, { withCredentials: true });
      setPolls(response.data.polls || []);
    } catch (error) { console.error(error); }
    finally { setPollsLoading(false); }
  };

  const handleToggleMembership = async () => {
    setJoinLoading(true);
    try {
      const response = await axios.post(`${API}/communities/${communityId}/join`, {}, { withCredentials: true });
      const data = response.data;
      if (data.pending) {
        setIsPending(true);
        toast.success("Demande envoyée — l'admin de la communauté va l'examiner.");
      } else {
        setIsPending(false);
        if (data.is_member) toast.success(`Bienvenue dans ${community?.name || 'la communauté'} !`);
        else toast('Tu as quitté la communauté');
        // Brief 27 oct LOT C.2 — accès immédiat au contenu après adhésion.
        await loadCommunity();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'action n'a pas pu aboutir");
    }
    finally { setJoinLoading(false); }
  };

  const handleKick = async (targetUserId) => {
    const reason = window.prompt('Raison du retrait (optionnel) :');
    if (reason === null) return;
    try {
      await axios.post(`${API}/communities/${communityId}/kick`, { user_id: targetUserId, reason }, { withCredentials: true });
      setAllMembers(prev => prev.filter(m => m.user_id !== targetUserId));
      setMembersTotal(prev => Math.max(0, prev - 1));
    } catch (error) { alert(error.response?.data?.detail || 'Erreur'); }
  };

  const handleBan = async (targetUserId) => {
    const reason = window.prompt('Raison du bannissement (optionnel) :');
    if (reason === null) return;
    if (!window.confirm('Confirmer le bannissement ? Ce membre ne pourra plus rejoindre.')) return;
    try {
      await axios.post(`${API}/communities/${communityId}/ban`, { user_id: targetUserId, reason }, { withCredentials: true });
      setAllMembers(prev => prev.filter(m => m.user_id !== targetUserId));
      setMembersTotal(prev => Math.max(0, prev - 1));
    } catch (error) { alert(error.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-12 w-12 animate-spin text-purple-500" /></div>;
  if (!community) return (
    <div className="text-center py-20" data-testid="community-not-found">
      <p className="text-gray-700 dark:text-gray-200 font-medium">Communauté introuvable</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Elle a peut-être été supprimée, ou le lien est incomplet.</p>
      <Button variant="outline" onClick={() => navigate('/app/community')}>Voir les communautés</Button>
    </div>
  );

  const isMember = community.is_member;
  const isCreator = community.is_creator;
  const isAdmin = community.is_admin;
  const admin = allMembers.find(m => m.role === 'admin') || community.creator;
  const locked = !!community.content_locked;

  const tabs = [
    { id: 'wall', label: 'Mur', icon: FileText },
    { id: 'questions', label: 'Questions', icon: HelpCircle },
    { id: 'polls', label: 'Sondages', icon: BarChart3 },
    { id: 'calendar', label: 'Calendrier', icon: Calendar },
  ];

  const shareModal = (
    <ShareModal
      isOpen={showShareModal}
      onClose={() => setShowShareModal(false)}
      shareType="community"
      shareData={{ id: communityId, title: community.name, description: community.description || `Rejoins la communauté ${community.name} sur OpenSapiens` }}
    />
  );

  // Brief 27 oct LOT C.1 — communauté privée, non membre : nom, description et
  // bouton de demande uniquement. Aucune publication, aucun membre, aucun calendrier.
  if (locked) {
    return (
      <div className="max-w-6xl mx-auto" data-testid="community-page">
        <CommunityHeader
          community={community}
          isCreator={isCreator}
          isMember={isMember}
          joinLoading={joinLoading}
          onlineCount={0}
          onEdit={() => setShowEditModal(true)}
          onToggleMembership={handleToggleMembership}
          isPending={isPending}
          onShare={() => setShowShareModal(true)}
        />
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-10 text-center" data-testid="community-locked">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Lock className="h-8 w-8 text-amber-600 dark:text-amber-300" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Communauté privée</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-md mx-auto">
            Les publications, les membres et le calendrier sont réservés aux membres. Envoie une demande : l'admin te répondra et tu seras notifié de sa décision.
          </p>
          <div className="mt-6">
            {isPending ? (
              <Button variant="outline" disabled data-testid="community-locked-pending">
                <Clock className="h-4 w-4 mr-2 text-amber-500" /> Demande envoyée — en attente de l'admin
              </Button>
            ) : (
              <Button onClick={handleToggleMembership} disabled={joinLoading} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-testid="community-locked-join-btn">
                {joinLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}Demander à rejoindre
              </Button>
            )}
          </div>
        </div>
        {shareModal}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="community-page">
      <CommunityHeader
        community={community}
        isCreator={isCreator}
        isMember={isMember}
        joinLoading={joinLoading}
        onlineCount={onlineCount}
        onEdit={() => setShowEditModal(true)}
        onToggleMembership={handleToggleMembership}
        isPending={isPending}
        onShare={() => setShowShareModal(true)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
            <div className="flex border-b border-gray-100 dark:border-gray-700">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3.5 text-xs sm:text-sm font-medium transition-all ${activeTab === tab.id ? 'text-purple-600 dark:text-purple-300 border-b-2 border-purple-600 bg-purple-50/50 dark:bg-purple-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`} data-testid={`community-tab-${tab.id}`}>
                  <tab.icon className="h-4 w-4" />{tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'wall' && (
            <WallTab
              communityId={communityId}
              community={community}
              isMember={isMember}
              isAdmin={isAdmin}
              posts={posts}
              postsLoading={postsLoading}
              currentUser={currentUser}
              navigate={navigate}
              joinLoading={joinLoading}
              onToggleMembership={handleToggleMembership}
              onRefresh={loadPosts}
              onPostCreated={() => { loadPosts(); setCommunity(prev => prev ? { ...prev, post_count: (prev.post_count || 0) + 1 } : prev); }}
              onPostDeleted={() => { setCommunity(prev => prev ? { ...prev, post_count: Math.max(0, (prev.post_count || 0) - 1) } : prev); }}
            />
          )}

          {activeTab === 'questions' && (
            <QuestionsTab
              communityId={communityId}
              isMember={isMember}
              isAdmin={isAdmin}
              currentUserId={currentUser?.user_id}
              questions={questions}
              loading={questionsLoading}
              filter={questionFilter}
              setFilter={setQuestionFilter}
              onRefresh={loadQuestions}
              navigate={navigate}
            />
          )}

          {activeTab === 'polls' && (
            <PollsTab
              communityId={communityId}
              isMember={isMember}
              isAdmin={isAdmin}
              polls={polls}
              loading={pollsLoading}
              onRefresh={loadPolls}
              currentUserId={currentUser?.user_id}
            />
          )}

          {activeTab === 'calendar' && (
            <CalendarTab
              communityId={communityId}
              events={events}
              isAdmin={isAdmin}
              isMember={isMember}
              onEventCreated={(e) => setEvents(prev => [e, ...prev])}
              onEventsRefresh={loadEvents}
            />
          )}
        </div>

        {/* Sidebar */}
        <CommunitySidebar
          community={community}
          admin={admin}
          allMembers={allMembers}
          membersTotal={membersTotal}
          onlineCount={onlineCount}
          isAdmin={isAdmin}
          isCreator={isCreator}
          currentUserId={currentUser?.user_id}
          communityId={communityId}
          navigate={navigate}
          onKick={handleKick}
          onBan={handleBan}
          onMembersChange={() => { loadMembers(); loadCommunity(); }}
        />
      </div>

      {showEditModal && <EditCommunityModal community={community} onClose={() => setShowEditModal(false)} onSave={(updated) => { setCommunity(prev => ({ ...prev, ...updated })); setShowEditModal(false); }} />}
      {shareModal}
    </div>
  );
};

export default CommunityPage;
